import {
  ClassGroupSummaryItem,
  RecitationOrderByType,
  RecitationStatusItem,
  RecitationStatusSummary,
  RecitationStatusType,
} from '../../../../constants/types/lms/recitation/type';
import TextWinds from '../../../common/TextWinds';
import { Button, Input, Select, Table } from 'antd';
import { ArrowDownIcon } from '@heroicons/react/24/outline';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import {
  deleteAssignmentByStudent,
  getRecitationStatusList,
} from '../../../../api/utils/lms/recitation/api';
import { useAppDispatch } from '../../../../store/hook';
import { getErrorMessages } from '../../../../api/utils/util-func';
import { openAlert, openConfirm } from '../../../../store/features/modalSlice';
import {
  CookieKey,
  CookieTypeJebsAiRecitation,
  CookieTypeUserInfo,
  createCookie,
} from '../../../../util/cookie';
import openPopupWindow from '../../../../util/popupWindow';
import { columns } from './StatusByClassColumns';

interface StatusByClassContentsProps {
  selectedClass: ClassGroupSummaryItem;
  handleBack: () => void;
}

const RECITATION_STATUS_FILTER_OPTIONS = [
  {
    label: '전체',
    value: RecitationStatusType.TOTAL,
  },
  {
    label: '합격',
    value: RecitationStatusType.PASSED,
  },
  {
    label: '불합격',
    value: RecitationStatusType.FAILED,
  },
  {
    label: 'AI분석 중',
    value: RecitationStatusType.ANALYZING,
  },
  {
    label: '진행 중',
    value: RecitationStatusType.IN_PROGRESS,
  },
  {
    label: '진행 전',
    value: RecitationStatusType.NOT_STARTED,
  },
] as const;

const RECITATION_SORT_OPTIONS = [
  {
    label: '응시일',
    value: RecitationOrderByType.ATTEMPTED_AT,
  },
  {
    label: '학생명',
    value: RecitationOrderByType.STUDENT_NAME,
  },
  {
    label: '레벨',
    value: RecitationOrderByType.LEVEL,
  },
] as const;

/**
 * 개편 LMS (교사) - AI인증제 관리 > AI인증제 현황 페이지 > 반별 과제 현황
 */
const StatusByClassContents = ({
  selectedClass,
  handleBack,
}: StatusByClassContentsProps) => {
  const { classId } = useParams();

  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    setSearchParams({ status: RecitationStatusType.TOTAL });
  }, []);

  const { status, orderBy } = useMemo(() => {
    return {
      status: searchParams.get('status') || undefined,
      orderBy: searchParams.get('orderBy') || undefined,
    };
  }, [searchParams]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [recitationStatusList, setRecitationStatusList] = useState<
    RecitationStatusItem[]
  >([]);
  const [statusSummary, setStatusSummary] =
    useState<RecitationStatusSummary | null>(null);

  const [selectedTodoIds, setSelectedTodoIds] = useState<number[]>([]);

  const [searchOption, setSearchOption] = useState('name');
  const [searchValue, setSearchValue] = useState('');

  const fetchRecitationStatusList = async () => {
    if (!selectedClass) return;

    try {
      setIsLoading(true);

      const response = await getRecitationStatusList({
        classId,
        classGroupId: selectedClass.classGroupId,
        unassignedGroup: selectedClass.classGroupId === null,
        result:
          status === RecitationStatusType.TOTAL
            ? undefined
            : RecitationStatusType[status as RecitationStatusType],
        orderBy: RecitationOrderByType[orderBy as RecitationOrderByType],
      });

      setRecitationStatusList(response.rows ?? []);
      setStatusSummary(response.summary ?? null);
      setSelectedTodoIds([]);
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedClass) {
      fetchRecitationStatusList();
    }
  }, [selectedClass]);

  useEffect(() => {
    fetchRecitationStatusList();
  }, [status, orderBy]);

  const filteredRecitationStatusList = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();

    if (!keyword) return recitationStatusList;

    return recitationStatusList.filter((item) =>
      item.studentName?.toLowerCase().includes(keyword),
    );
  }, [recitationStatusList, searchValue]);

  const filteredStatusSummary = useMemo(() => {
    return {
      failed: filteredRecitationStatusList.filter(
        (item) => item.status === 'FAILED',
      ).length,
      others: filteredRecitationStatusList.filter(
        (item) => item.status !== 'FAILED' && item.status !== 'PASSED',
      ).length,
      passed: filteredRecitationStatusList.filter(
        (item) => item.status === 'PASSED',
      ).length,
      total: filteredRecitationStatusList.length,
    };
  }, [filteredRecitationStatusList]);

  const handleFilterToggle = (
    nextStatus: keyof typeof RecitationStatusType,
  ) => {
    if (status === nextStatus) {
      searchParams.set('status', 'TOTAL');
      setSearchParams(searchParams);
    } else {
      searchParams.set('status', nextStatus);
      setSearchParams(searchParams);
    }
  };

  const handleSortToggle = (
    nextOrderBy: keyof typeof RecitationOrderByType,
  ) => {
    if (orderBy === nextOrderBy) {
      searchParams.delete('orderBy');
      setSearchParams(searchParams);
    } else {
      searchParams.set('orderBy', nextOrderBy);
      setSearchParams(searchParams);
    }
  };

  const { setCookie } = createCookie<CookieTypeJebsAiRecitation>(
    CookieKey.JEBS_AI_RECITATION,
  );
  const { getCookie: getUserInfoCookie } = createCookie<CookieTypeUserInfo>(
    CookieKey.USER_INFO,
  );
  const { removeCookie: removeNewRecitationCookie } =
    createCookie<CookieTypeJebsAiRecitation>(CookieKey.JEBS_AI_RECITATION);

  const handleResultClick = (assignment: RecitationStatusItem) => {
    try {
      removeNewRecitationCookie();
    } catch (error) {
      // 쿠키가 없는 경우 무시
      // console.error(error);
    }

    const { id, uid } = getUserInfoCookie();

    setCookie(
      {
        student: {
          id: assignment.studentId,
          uid: assignment.studentUid,
        },
        teacher: {
          id: id,
          uid: uid,
          classId: classId,
        },
        todo: {
          id: assignment.todoId,
          type: 'JEBS_AI_RECITATION',
        },
        accessToken: localStorage.getItem('accessToken'),
      },
      1 / 24,
    );
    openPopupWindow(assignment.resultUrl, 1440, 1440 / 1080);
  };

  const handleDelete = async (assignment: RecitationStatusItem) => {
    dispatch(
      openConfirm({
        message: (
          <div className="flex flex-col md:my-3">
            <div className="text-base">삭제하시면 되돌릴 수 없습니다.</div>

            <div className="text-base">정말 삭제하시겠습니까?</div>
          </div>
        ),
        confirmText: '삭제하기',
        cancelText: '닫기',
        onConfirm: async () => {
          try {
            await deleteAssignmentByStudent({
              classId,
              todoId: assignment.todoId,
            });

            await fetchRecitationStatusList();
          } catch (error) {
            dispatch(openAlert({ message: getErrorMessages(error) }));
          }
        },
      }),
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedTodoIds.length <= 0) {
      dispatch(
        openAlert({
          message: '삭제할 과제를 선택해주세요.',
        }),
      );
      return;
    }

    dispatch(
      openConfirm({
        message: (
          <div className="flex flex-col md:my-3">
            <div className="text-base">
              선택한 과제 {selectedTodoIds.length}건을 삭제합니다.
            </div>

            <div className="text-base">삭제하시면 되돌릴 수 없습니다.</div>

            <div className="text-base">정말 삭제하시겠습니까?</div>
          </div>
        ),
        confirmText: '삭제하기',
        cancelText: '닫기',
        onConfirm: async () => {
          try {
            await Promise.all(
              selectedTodoIds.map((todoId) =>
                deleteAssignmentByStudent({
                  classId,
                  todoId,
                }),
              ),
            );

            setSelectedTodoIds([]);
            await fetchRecitationStatusList();
          } catch (error) {
            dispatch(openAlert({ message: getErrorMessages(error) }));
          }
        },
      }),
    );
  };

  return (
    <>
      <div className="mb-5 flex items-center gap-3">
        <Button
          type="default"
          className="flex items-center gap-2 rounded-lg px-5"
          onClick={() => {
            handleBack();
            setSearchParams({});
          }}
        >
          ← 반 목록으로
        </Button>

        <div className="min-w-0">
          <TextWinds type="title_h3" className="truncate">
            {selectedClass.classGroupName}
          </TextWinds>
        </div>
      </div>

      <div className="mb-5 flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="flex min-w-0 flex-col gap-10 xl:flex-row xl:flex-wrap xl:items-center">
          <div className="flex w-full gap-2 xl:w-auto">
            <Select
              className="w-[100px] shrink-0"
              options={[{ value: 'name', label: '학생이름' }]}
              value={searchOption}
              onChange={(v) => setSearchOption(v)}
            />

            <Input
              type="search"
              className="min-w-0 flex-1 xl:w-[200px] xl:flex-none"
              placeholder="검색어를 입력하세요"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>

          <div className="grid w-full grid-cols-2 gap-2 xl:flex xl:w-auto xl:items-center">
            {RECITATION_STATUS_FILTER_OPTIONS.map((option) => {
              const isActive = status === option.value;

              return (
                <Button
                  key={option.value}
                  type="default"
                  className={[
                    'rounded-lg',
                    isActive
                      ? '!border-purple5 !bg-purple1 !text-purple5'
                      : '!border-gray3 !bg-white hover:!border-purple3 hover:!bg-gray0',
                  ].join(' ')}
                  onClick={() => handleFilterToggle(option.value)}
                >
                  <span className="flex items-center justify-center gap-0.5">
                    {option.label}
                  </span>
                </Button>
              );
            })}
          </div>

          <div className="grid w-full grid-cols-2 gap-2 xl:flex xl:w-auto xl:items-center">
            정렬
            {RECITATION_SORT_OPTIONS.map((option) => {
              const isActive = orderBy === option.value;

              return (
                <Button
                  key={option.value}
                  type="default"
                  className={[
                    'rounded-lg',
                    isActive
                      ? '!border-purple5 !bg-purple1 !text-purple5'
                      : '!border-gray3 !bg-white hover:!border-purple3 hover:!bg-gray0',
                  ].join(' ')}
                  onClick={() => handleSortToggle(option.value)}
                >
                  <span className="flex items-center justify-center gap-0.5">
                    <ArrowDownIcon className="h-4 w-4" />
                    {option.label}
                  </span>
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
          <TextWinds type="content_body3" color="gray7" className="font-bold">
            총 {filteredStatusSummary.total}건
          </TextWinds>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <TextWinds type="caption1" className="font-bold text-green-700">
              합격 {filteredStatusSummary.passed}건
            </TextWinds>

            <TextWinds type="caption1" className="font-bold text-red-600">
              불합격 {filteredStatusSummary.failed}건
            </TextWinds>

            <TextWinds type="caption1" className="font-bold text-gray7">
              기타 {filteredStatusSummary.others}건
            </TextWinds>

            <Button
              type="default"
              danger
              disabled={selectedTodoIds.length <= 0}
              className={[
                'rounded-lg px-3 text-xs font-bold',
                selectedTodoIds.length > 0
                  ? '!border-red-300 !bg-white !text-red-500 hover:!border-red-400 hover:!bg-red-50 hover:!text-red-600'
                  : '!cursor-not-allowed !border-gray3 !bg-gray1 !text-gray5',
              ].join(' ')}
              onClick={handleDeleteSelected}
            >
              선택 삭제
              {selectedTodoIds.length > 0 && ` (${selectedTodoIds.length})`}
            </Button>
          </div>
        </div>

        <Table
          loading={isLoading}
          className="overflow-hidden rounded-xl border border-gray2 bg-white"
          dataSource={filteredRecitationStatusList}
          columns={columns({
            recitationStatusList: filteredRecitationStatusList,
            handleResultClick,
            handleDelete,
          })}
          rowKey={(row) => `${row.todoId}-${row.attemptedAt}`}
          rowSelection={{
            selectedRowKeys: selectedTodoIds,
            onChange: (selectedRowKeys) => {
              setSelectedTodoIds(selectedRowKeys as number[]);
            },
            getCheckboxProps: (record) => ({
              disabled: record.status !== 'NOT_STARTED',
            }),
          }}
          scroll={{ x: 'max-content' }}
          size="small"
          pagination={false}
        />
      </div>
    </>
  );
};

export default StatusByClassContents;
